import { ScatterChart } from "@mantine/charts";
import { Center, Skeleton, Stack, Title, useMatches } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { IconSquareRotatedFilled } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { TransactionsTable } from "../TransactionsTable";
import { CarTransactionsTooltip } from "./components";
import { getDataAndDomain } from "./utils";

import { EmptyStateRevamped } from "@/components/elements";
import { defaultCurrency } from "@/lib/currency";
import { Currency } from "@/types/currency";
import { formatCurrency } from "@/utils/formatEuro";

import type { AuctionCarTransaction } from "@/types/auctions";
import type { FC } from "react";

export type CarTransactionsProps = {
  loading?: boolean;
  marketCarId?: string;
  namespace?: string;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSearch?: (query: string) => void;
  page?: number;
  pageSize?: number;
  searchValue?: string;
  totalTransactions?: number;
  transactions?: AuctionCarTransaction[];
};

export const CarTransactions: FC<CarTransactionsProps> = ({
  loading = false,
  marketCarId,
  namespace,
  onPageChange,
  onPageSizeChange,
  onSearch,
  page,
  pageSize,
  searchValue,
  totalTransactions,
  transactions = [],
}) => {
  const { t } = useTranslation();

  const isMobile = useMatches({ base: true, md: false });

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const { data, domain } = useMemo(
    () => getDataAndDomain(transactions, currency),
    [transactions, currency]
  );

  const isSearching = Boolean(searchValue?.trim());
  const hasChartData = data.length > 0;

  const chartValueFormatter = useMemo(
    () => ({
      x: (value: number) => dayjs.unix(value).format("MMM YYYY"),
      y: (value: number) =>
        formatCurrency(value, currency.code, {
          notation: "compact" as const,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }),
    }),
    [currency.code]
  );

  const chartTooltipProps = useMemo(
    () => ({
      content: ({ payload }: { payload?: ReadonlyArray<Record<string, any>> }) => (
        <CarTransactionsTooltip payload={payload ? [...payload] : undefined} />
      ),
      cursor: { strokeDasharray: "5 5" },
    }),
    []
  );

  const chartXAxisProps = useMemo(() => ({ domain }), [domain]);
  const chartScatterProps = useMemo(() => ({ shape: <IconSquareRotatedFilled size={8} /> }), []);

  return (
    <Stack>
      <Title order={3}>{t("components.CarTransactions.title")}</Title>
      <Skeleton visible={loading}>
        <Stack>
          {hasChartData ? (
            <ScatterChart
              h={300}
              data={data}
              dataKey={{ x: "date", y: "value" }}
              strokeDasharray="0"
              tickLine="none"
              xAxisProps={chartXAxisProps}
              scatterProps={chartScatterProps}
              valueFormatter={chartValueFormatter}
              tooltipProps={chartTooltipProps}
            />
          ) : (
            <Center h={isMobile ? "18em" : "20em"}>
              <EmptyStateRevamped
                title={isSearching ? undefined : t("common.noData")}
                description={isSearching ? undefined : ""}
              />
            </Center>
          )}
          <TransactionsTable
            columns={["date", "type", "status", "price", "seller", "event", "more"]}
            marketCarId={marketCarId}
            namespace={namespace}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onSearch={onSearch}
            page={page}
            pageSize={pageSize}
            searchValue={searchValue}
            suppressEmptyState={!hasChartData}
            totalTransactions={totalTransactions}
            transactions={transactions}
          />
        </Stack>
      </Skeleton>
    </Stack>
  );
};
